// CIFP / NASR fixes layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.
//
// Uses the LayerFamilySpec shape introduced in batch 9.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { ensureFixIcons } from '../fixIcons';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the fixes source + its layers. Idempotent on the source.
 */
export function addFixesLayers(map: MlMap): void {
    if (!map.getSource('fixes')) {
      map.addSource('fixes', { type: 'geojson', data: EMPTY_FC });
      void ensureFixIcons(map);
      map.addLayer({
        id: 'fixes-icon',
        type: 'symbol',
        source: 'fixes',
        minzoom: 8,
        layout: {
          // Hidden by default; the 'fixes' layer toggle in the
          // LayerSheet flips visibility.
          visibility: 'none',
          'icon-image': 'fix-tri',
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            8, 0.7,
            11, 1.0,
            14, 1.3,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: 'fixes-label',
        type: 'symbol',
        source: 'fixes',
        minzoom: 9,
        layout: {
          visibility: 'none',
          'text-field': ['get', 'ident'],
          'text-size': 9,
          'text-offset': [0, 0.9],
          'text-anchor': 'top',
          'text-font': ['Noto Sans Regular'],
        },
        paint: {
          'text-color': '#bfdbfe',
          'text-halo-color': '#0b0d0f',
          'text-halo-width': 1.2,
        },
      });
    }
}

registerLayerFamily({
  id: 'fixes',
  label: 'CIFP / NASR fixes',
  sourceId: 'fixes',
  layerIds: ['fixes-icon', 'fixes-label'],
  mount: (map) => addFixesLayers(map),
  visibilitySelector: (l) => l.fixes,
  applyState: (map) => {
    if (!map.getLayer('fixes-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.fixes;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('fixes-icon', 'visibility', v);
    map.setLayoutProperty('fixes-label', 'visibility', v);
  },
  // bboxRefresh + toggle-on handled by MapView's core bbox-fetch loop
  // (fixes/airports/waypoints use fetchBbox, not overlayRuntime).
});
